import { useState, useEffect, useRef, useCallback } from "react";

// ─── Intersection Observer Hook ───
function useOnScreen(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ─── Animated Counter ───
function Counter({ end, suffix = "", prefix = "", duration = 2000 }) {
  const [count, setCount] = useState(0);
  const [ref, visible] = useOnScreen(0.3);
  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [visible, end, duration]);
  return <span ref={ref}>{prefix}{count}{suffix}</span>;
}

// ─── Reveal Wrapper ───
function Reveal({ children, delay = 0, direction = "up", style = {} }) {
  const [ref, visible] = useOnScreen(0.1);
  const transforms = { up: "translateY(60px)", down: "translateY(-60px)", left: "translateX(60px)", right: "translateX(-60px)", none: "none" };
  return (
    <div ref={ref} style={{
      ...style,
      opacity: visible ? 1 : 0,
      transform: visible ? "none" : transforms[direction],
      transition: `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}s`
    }}>{children}</div>
  );
}

export default function CreditGroupSolutionsWebsite() {
  const [scrollY, setScrollY] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ assetSize: "", monthlyVolume: "", types: { cre: false, ci: false, smallBiz: false, consumer: false }, name: "", email: "", bank: "" });
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  }, []);

  const navOpacity = Math.min(scrollY / 200, 1);

  return (
    <div style={{ fontFamily: "'Outfit', sans-serif", color: "#e8e4df", background: "#08090c", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,500&family=Outfit:wght@200;300;400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        ::selection { background: rgba(198,163,91,0.3); }
        
        @keyframes heroReveal { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes lineGrow { from { width: 0; } to { width: 80px; } }
        @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-12px); } }
        @keyframes memoFloat { 0%, 100% { transform: rotate(1.5deg) translateY(0px); } 50% { transform: rotate(1.5deg) translateY(-10px); } }
        @keyframes grain { 0% { transform: translate(0,0) } 10% { transform: translate(-2%,-2%) } 20% { transform: translate(2%,2%) } 30% { transform: translate(-1%,1%) } 40% { transform: translate(1%,-1%) } 50% { transform: translate(-2%,2%) } 60% { transform: translate(2%,-2%) } 70% { transform: translate(-1%,-1%) } 80% { transform: translate(1%,1%) } 90% { transform: translate(-2%,-2%) } 100% { transform: translate(0,0) } }
        @keyframes shimmerLine { 0% { left: -100%; } 100% { left: 200%; } }
        @keyframes pulseGlow { 0%, 100% { box-shadow: 0 0 0 0 rgba(198,163,91,0.3); } 50% { box-shadow: 0 0 30px 10px rgba(198,163,91,0.08); } }

        .nav-link { position: relative; color: #8a8578; transition: color 0.3s; cursor: pointer; font-size: 13px; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 500; background: none; border: none; font-family: 'Outfit', sans-serif; }
        .nav-link:hover { color: #c6a35b; }
        .nav-link::after { content: ''; position: absolute; bottom: -4px; left: 0; width: 0; height: 1px; background: #c6a35b; transition: width 0.3s; }
        .nav-link:hover::after { width: 100%; }
        
        .cta-primary { display: inline-flex; align-items: center; gap: 10px; padding: 16px 36px; background: linear-gradient(135deg, #c6a35b 0%, #d4b56c 50%, #c6a35b 100%); color: #08090c; font-weight: 600; font-size: 14px; letter-spacing: 0.08em; text-transform: uppercase; border: none; border-radius: 2px; cursor: pointer; transition: all 0.4s cubic-bezier(0.16,1,0.3,1); font-family: 'Outfit', sans-serif; position: relative; overflow: hidden; }
        .cta-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 40px rgba(198,163,91,0.3); letter-spacing: 0.12em; }
        .cta-primary::before { content: ''; position: absolute; top: 0; left: -100%; width: 60%; height: 100%; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent); animation: shimmerLine 3s ease-in-out infinite; }
        
        .cta-outline { display: inline-flex; align-items: center; gap: 10px; padding: 15px 35px; background: transparent; color: #c6a35b; font-weight: 500; font-size: 14px; letter-spacing: 0.08em; text-transform: uppercase; border: 1px solid rgba(198,163,91,0.4); border-radius: 2px; cursor: pointer; transition: all 0.4s; font-family: 'Outfit', sans-serif; }
        .cta-outline:hover { border-color: #c6a35b; background: rgba(198,163,91,0.06); transform: translateY(-2px); }
        
        .service-card { padding: 40px 36px; border: 1px solid rgba(198,163,91,0.1); background: rgba(255,255,255,0.015); transition: all 0.5s cubic-bezier(0.16,1,0.3,1); position: relative; overflow: hidden; }
        .service-card:hover { border-color: rgba(198,163,91,0.4); background: rgba(198,163,91,0.03); transform: translateY(-6px); }
        .service-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, transparent, #c6a35b, transparent); opacity: 0; transition: opacity 0.5s; }
        .service-card:hover::before { opacity: 1; }
        .memo-float { transition: transform 0.5s cubic-bezier(0.16,1,0.3,1), box-shadow 0.5s ease; }
        .memo-float:hover { transform: rotate(0deg) translateY(-10px) !important; box-shadow: 0 40px 100px rgba(0,0,0,0.6), 0 0 80px rgba(198,163,91,0.08) !important; }

        .step-card { position: relative; padding: 44px 36px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); transition: all 0.4s; }
        .step-card:hover { background: rgba(198,163,91,0.03); border-color: rgba(198,163,91,0.15); }

        .stat-item { text-align: center; padding: 32px 20px; }
        
        .trust-badge { padding: 24px 28px; border: 1px solid rgba(255,255,255,0.06); background: rgba(255,255,255,0.02); display: flex; align-items: center; gap: 16px; transition: all 0.3s; }
        .trust-badge:hover { border-color: rgba(198,163,91,0.2); }

        .mobile-menu { position: fixed; top: 0; right: 0; bottom: 0; width: 300px; background: #0d0e12; border-left: 1px solid rgba(198,163,91,0.1); z-index: 1001; padding: 80px 40px; display: flex; flex-direction: column; gap: 24px; transition: transform 0.4s cubic-bezier(0.16,1,0.3,1); }
        
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-toggle { display: flex !important; }
        }
        @media (min-width: 769px) {
          .mobile-toggle { display: none !important; }
          .mobile-menu { display: none !important; }
        }
      `}</style>

      {/* ═══ GRAIN OVERLAY ═══ */}
      <div style={{ position: "fixed", inset: 0, zIndex: 9999, pointerEvents: "none", opacity: 0.03,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        animation: "grain 8s steps(10) infinite" }} />

      {/* ═══ NAVIGATION ═══ */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
        padding: "0 clamp(24px, 5vw, 80px)",
        height: "72px", display: "flex", alignItems: "center", justifyContent: "space-between",
        background: `rgba(8,9,12,${navOpacity * 0.95})`,
        borderBottom: navOpacity > 0.5 ? "1px solid rgba(198,163,91,0.08)" : "1px solid transparent",
        backdropFilter: navOpacity > 0.3 ? "blur(20px)" : "none",
        transition: "border-color 0.3s, backdrop-filter 0.3s"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px", cursor: "pointer" }} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <div style={{ width: "34px", height: "34px", border: "1.5px solid #c6a35b", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", color: "#c6a35b", fontWeight: 700, fontFamily: "'Playfair Display', serif", letterSpacing: "-0.02em" }}>CGS</div>
          <span style={{ fontSize: "13px", letterSpacing: "0.18em", color: "#8a8578", fontWeight: 500, textTransform: "uppercase" }}>Credit Group Solutions</span>
        </div>
        <div className="desktop-nav" style={{ display: "flex", alignItems: "center", gap: "36px" }}>
          {[["Services", "services"], ["Platform", "platform"], ["Process", "process"], ["Pricing", "pricing"], ["Security", "security"]].map(([label, id]) => (
            <button key={id} className="nav-link" onClick={() => scrollTo(id)}>{label}</button>
          ))}
          <button className="cta-primary" onClick={() => scrollTo("contact")} style={{ padding: "10px 24px", fontSize: "12px" }}>Start Pilot</button>
        </div>
        <button className="mobile-toggle" onClick={() => setMenuOpen(!menuOpen)}
          style={{ display: "none", flexDirection: "column", gap: "5px", background: "none", border: "none", cursor: "pointer", padding: "8px" }}>
          {[0,1,2].map(i => <div key={i} style={{ width: "22px", height: "1.5px", background: "#c6a35b", transition: "all 0.3s",
            transform: menuOpen ? (i===0 ? "rotate(45deg) translate(4px,5px)" : i===2 ? "rotate(-45deg) translate(4px,-5px)" : "scaleX(0)") : "none" }} />)}
        </button>
      </nav>

      {/* Mobile menu */}
      <div className="mobile-menu" style={{ transform: menuOpen ? "translateX(0)" : "translateX(100%)" }}>
        {[["Services", "services"], ["Platform", "platform"], ["Process", "process"], ["Pricing", "pricing"], ["Security", "security"], ["Contact", "contact"]].map(([label, id]) => (
          <button key={id} className="nav-link" onClick={() => scrollTo(id)} style={{ fontSize: "18px", textAlign: "left" }}>{label}</button>
        ))}
      </div>
      {menuOpen && <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000 }} onClick={() => setMenuOpen(false)} />}

      {/* ═══ HERO ═══ */}
      <section style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center",
        padding: "120px clamp(24px, 8vw, 120px) 80px",
        position: "relative", overflow: "hidden"
      }}>
        {/* Ambient glow */}
        <div style={{ position: "absolute", top: "-20%", right: "-10%", width: "60vw", height: "60vw", borderRadius: "50%",
          background: "radial-gradient(circle, rgba(198,163,91,0.04) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "10%", left: "-5%", width: "40vw", height: "40vw", borderRadius: "50%",
          background: "radial-gradient(circle, rgba(198,163,91,0.02) 0%, transparent 70%)", pointerEvents: "none" }} />

        {/* Vertical line decoration */}
        <div style={{ position: "absolute", top: 0, right: "clamp(40px, 12vw, 180px)", width: "1px", height: "45vh",
          background: "linear-gradient(to bottom, transparent, rgba(198,163,91,0.15), transparent)" }} />

        <div style={{ maxWidth: "900px", position: "relative", zIndex: 2 }}>
          <div style={{
            opacity: mounted ? 1 : 0, transform: mounted ? "none" : "translateY(40px)",
            transition: "all 1s cubic-bezier(0.16,1,0.3,1) 0.2s"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "32px" }}>
              <div style={{ width: "80px", height: "1px", background: "#c6a35b", animation: mounted ? "lineGrow 1s ease 0.8s both" : "none" }} />
              <span style={{ fontSize: "12px", letterSpacing: "0.25em", textTransform: "uppercase", color: "#c6a35b", fontWeight: 500 }}>
                Credit Analysis for Private Lenders & Commercial Banks
              </span>
            </div>
          </div>

          <h1 style={{
            fontFamily: "'Playfair Display', serif", fontWeight: 400,
            fontSize: "clamp(40px, 6.5vw, 82px)", lineHeight: 1.05, letterSpacing: "-0.02em",
            color: "#f0ece6", marginBottom: "32px",
            opacity: mounted ? 1 : 0, transform: mounted ? "none" : "translateY(40px)",
            transition: "all 1s cubic-bezier(0.16,1,0.3,1) 0.4s"
          }}>
            Your credit team,<br />
            <span style={{
              fontStyle: "italic", color: "#c6a35b",
              textShadow: "0 0 80px rgba(198,163,91,0.15)"
            }}>extended.</span>
          </h1>

          <p style={{
            fontSize: "clamp(16px, 1.8vw, 20px)", color: "#8a8578", lineHeight: 1.7,
            maxWidth: "560px", fontWeight: 300, marginBottom: "48px",
            opacity: mounted ? 1 : 0, transform: mounted ? "none" : "translateY(40px)",
            transition: "all 1s cubic-bezier(0.16,1,0.3,1) 0.6s"
          }}>
            Your business partner for AI-powered credit analysis, document processing, and committee-ready memorandums — built for private lenders and commercial banks.
          </p>

          <div style={{
            display: "flex", gap: "16px", flexWrap: "wrap",
            opacity: mounted ? 1 : 0, transform: mounted ? "none" : "translateY(40px)",
            transition: "all 1s cubic-bezier(0.16,1,0.3,1) 0.8s"
          }}>
            <button className="cta-primary" onClick={() => scrollTo("contact")}>Start Your Pilot →</button>
            <button className="cta-outline" onClick={() => scrollTo("process")}>See How It Works</button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div style={{
          position: "absolute", bottom: "40px", left: "50%", transform: "translateX(-50%)",
          display: "flex", flexDirection: "column", alignItems: "center", gap: "8px",
          opacity: mounted ? 0.4 : 0, transition: "opacity 1s 1.5s"
        }}>
          <span style={{ fontSize: "10px", letterSpacing: "0.2em", textTransform: "uppercase", color: "#5a5549" }}>Scroll</span>
          <div style={{ width: "1px", height: "40px", background: "linear-gradient(to bottom, #5a5549, transparent)", animation: "float 3s ease-in-out infinite" }} />
        </div>
      </section>

      {/* ═══ SOCIAL PROOF BAR ═══ */}
      <section style={{ borderTop: "1px solid rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.04)", background: "rgba(255,255,255,0.01)" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 clamp(24px, 5vw, 80px)",
          display: "flex", justifyContent: "center", flexWrap: "wrap" }}>
          {[
            { num: 24, suffix: "hr", label: "Turnaround" },
            { num: 10, suffix: "+", label: "Years Commercial Banking Experience" },
            { num: 85, suffix: "%", label: "Reduction In Underwriting Turnaround Time" },
            { num: "Zero", label: "Long-Term Contracts" }
          ].map((s, i) => (
            <div key={i} className="stat-item" style={{ flex: "1 1 140px", borderRight: i < 3 ? "1px solid rgba(255,255,255,0.04)" : "none" }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(28px, 3.5vw, 40px)", color: "#c6a35b", fontWeight: 400, marginBottom: "6px" }}>
                {typeof s.num === "string" ? s.num : <Counter end={s.num} prefix={s.prefix || ""} suffix={s.suffix} />}
              </div>
              <div style={{ fontSize: "11px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#5a5549", fontWeight: 500 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ PROBLEM ═══ */}
      <section style={{ padding: "clamp(80px, 12vw, 160px) clamp(24px, 8vw, 120px)", position: "relative" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <Reveal>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
              <div style={{ width: "40px", height: "1px", background: "#c6a35b" }} />
              <span style={{ fontSize: "12px", letterSpacing: "0.25em", textTransform: "uppercase", color: "#c6a35b", fontWeight: 500 }}>The Challenge</span>
            </div>
          </Reveal>
          
          <div style={{ display: "flex", gap: "clamp(40px, 6vw, 100px)", flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ flex: "1 1 400px", minWidth: "300px" }}>
              <Reveal delay={0.1}>
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(30px, 4vw, 46px)", fontWeight: 400, lineHeight: 1.15, color: "#f0ece6", marginBottom: "28px" }}>
                  Private lenders and commercial banks are <span style={{ fontStyle: "italic", color: "#c6a35b" }}>stretched thin</span> on credit analysis.
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p style={{ fontSize: "17px", color: "#8a8578", lineHeight: 1.8, fontWeight: 300 }}>
                  Relationship managers are sacrificing valuable time waiting on an outdated credit department process to deliver analysis before they can even begin their own memos. With our guaranteed 24-hour turnaround, relationship managers can stay focused on what matters most — the client relationship. Our goal is to empower your team with fast, accurate data to help make better lending decisions.
                </p>
              </Reveal>
            </div>

            <div style={{ flex: "1 1 300px", minWidth: "260px", display: "flex", flexDirection: "column", gap: "20px" }}>
              {[
                { num: "1–2", label: "analysts handling your entire loan portfolio" },
                { num: "5–7", label: "days average turnaround on credit analysis" },
                { num: "60%", label: "of analyst time spent on routine spreading & memos" }
              ].map((item, i) => (
                <Reveal key={i} delay={0.15 * (i + 1)} direction="left">
                  <div style={{ display: "flex", gap: "20px", alignItems: "flex-start", padding: "20px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "32px", color: "#c6a35b", fontWeight: 400, minWidth: "70px" }}>{item.num}</span>
                    <span style={{ fontSize: "15px", color: "#8a8578", lineHeight: 1.6, paddingTop: "8px" }}>{item.label}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SERVICES ═══ */}
      <section id="services" style={{ padding: "clamp(80px, 12vw, 160px) clamp(24px, 8vw, 120px)", background: "rgba(255,255,255,0.01)", borderTop: "1px solid rgba(255,255,255,0.03)" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: "64px" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
                <div style={{ width: "40px", height: "1px", background: "#c6a35b" }} />
                <span style={{ fontSize: "12px", letterSpacing: "0.25em", textTransform: "uppercase", color: "#c6a35b", fontWeight: 500 }}>What We Deliver</span>
                <div style={{ width: "40px", height: "1px", background: "#c6a35b" }} />
              </div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(30px, 4vw, 46px)", fontWeight: 400, color: "#f0ece6" }}>
                Business and risk management,<br /><span style={{ fontStyle: "italic", color: "#c6a35b" }}>built for your lending operation.</span>
              </h2>
            </div>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "24px" }}>
            {[
              { icon: "⊞", title: "Financial Spreading", desc: "Raw statements transformed into standardized 3-year spreads. Every line item mapped, verified, and formatted for your systems.", tag: "Core" },
              { icon: "◈", title: "Credit Analysis", desc: "Complete ratio packages with industry benchmarks, trend indicators, and peer comparisons. DSCR, leverage, liquidity, and profitability — all covered.", tag: "Core" },
              { icon: "◱", title: "Credit Memorandums", desc: "Formal, committee-ready credit memos with executive summary, financial analysis, risk assessment, and recommendation.", tag: "Premium" },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 0.12}>
                <div className="service-card">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px" }}>
                    <span style={{ fontSize: "32px", color: "#c6a35b", opacity: 0.8 }}>{s.icon}</span>
                    <span style={{ fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#5a5549", border: "1px solid rgba(255,255,255,0.08)", padding: "4px 10px", fontWeight: 500 }}>{s.tag}</span>
                  </div>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "22px", fontWeight: 400, color: "#f0ece6", marginBottom: "14px" }}>{s.title}</h3>
                  <p style={{ fontSize: "14px", color: "#8a8578", lineHeight: 1.75, fontWeight: 300 }}>{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PLATFORM FEATURES ═══ */}
      <section id="platform" style={{ padding: "clamp(80px, 12vw, 160px) clamp(24px, 8vw, 120px)", position: "relative" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: "72px" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
                <div style={{ width: "40px", height: "1px", background: "#c6a35b" }} />
                <span style={{ fontSize: "12px", letterSpacing: "0.25em", textTransform: "uppercase", color: "#c6a35b", fontWeight: 500 }}>Our Platform</span>
                <div style={{ width: "40px", height: "1px", background: "#c6a35b" }} />
              </div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(30px, 4vw, 46px)", fontWeight: 400, color: "#f0ece6" }}>
                Intelligent tools for <span style={{ fontStyle: "italic", color: "#c6a35b" }}>modern</span> lending.
              </h2>
            </div>
          </Reveal>

          {/* ── Document Processing ── */}
          <div style={{ display: "flex", gap: "clamp(40px, 6vw, 100px)", flexWrap: "wrap", alignItems: "center", marginBottom: "clamp(80px, 10vw, 140px)" }}>
            <div style={{ flex: "1 1 400px", minWidth: "300px" }}>
              <Reveal>
                <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
                  <div style={{ width: "40px", height: "1px", background: "#c6a35b" }} />
                  <span style={{ fontSize: "12px", letterSpacing: "0.25em", textTransform: "uppercase", color: "#c6a35b", fontWeight: 500 }}>Document Processing</span>
                </div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(26px, 3.5vw, 38px)", fontWeight: 400, lineHeight: 1.15, color: "#f0ece6", marginBottom: "24px" }}>
                  Every document.<br /><span style={{ fontStyle: "italic", color: "#c6a35b" }}>One unified view.</span>
                </h3>
              </Reveal>
              <Reveal delay={0.1}>
                <p style={{ fontSize: "16px", color: "#8a8578", lineHeight: 1.8, fontWeight: 300, marginBottom: "32px" }}>
                  Upload tax returns, P&Ls, CPA-prepared statements, and K-1 schedules. Our platform ingests, cross-references, and reconciles across personal and business entities — eliminating double-counting and manual spreading.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
                  {[
                    { value: "90%", label: "Faster Spreading" },
                    { value: "All", label: "File Types Accepted" },
                    { value: "24hr", label: "Turnaround" },
                  ].map((m, i) => (
                    <div key={i} style={{ textAlign: "center" }}>
                      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "28px", color: "#c6a35b", fontWeight: 400, marginBottom: "4px" }}>{m.value}</div>
                      <div style={{ fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#5a5549", fontWeight: 500 }}>{m.label}</div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            <div style={{ flex: "1 1 340px", minWidth: "280px", display: "flex", flexDirection: "column", gap: "12px" }}>
              {[
                { icon: "📄", label: "1040 Tax Returns" },
                { icon: "📊", label: "Business P&L Statements" },
                { icon: "📋", label: "CPA-Prepared Financials" },
                { icon: "📑", label: "K-1 Schedules & Partnerships" },
              ].map((d, i) => (
                <Reveal key={i} delay={i * 0.08} direction="left">
                  <div style={{
                    display: "flex", alignItems: "center", gap: "16px",
                    padding: "16px 20px", border: "1px solid rgba(198,163,91,0.1)", background: "rgba(255,255,255,0.015)",
                    transition: "all 0.4s", cursor: "default"
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(198,163,91,0.35)"; e.currentTarget.style.transform = "translateX(6px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(198,163,91,0.1)"; e.currentTarget.style.transform = "none"; }}
                  >
                    <span style={{ fontSize: "20px" }}>{d.icon}</span>
                    <span style={{ fontSize: "14px", fontWeight: 400, color: "#e8e4df", flex: 1 }}>{d.label}</span>
                    <span style={{ fontSize: "14px", color: "#c6a35b" }}>→</span>
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.4}>
                <div style={{
                  marginTop: "8px", padding: "16px 20px", textAlign: "center",
                  border: "1px solid rgba(198,163,91,0.2)", background: "rgba(198,163,91,0.04)"
                }}>
                  <span style={{ fontSize: "12px", letterSpacing: "0.12em", textTransform: "uppercase", color: "#c6a35b", fontWeight: 500 }}>
                    ✓ Unified Credit Analysis Output
                  </span>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Subtle divider */}
          <div style={{ height: "1px", background: "linear-gradient(90deg, transparent, rgba(198,163,91,0.12), transparent)", marginBottom: "clamp(80px, 10vw, 140px)" }} />

          {/* ── AI-Powered Credit Analysis ── */}
          <div style={{ display: "flex", gap: "clamp(40px, 6vw, 100px)", flexWrap: "wrap", alignItems: "center", marginBottom: "clamp(80px, 10vw, 140px)" }}>
            <div style={{ flex: "1 1 340px", minWidth: "280px", order: 1 }}>
              <div style={{
                border: "1px solid rgba(198,163,91,0.12)", background: "rgba(255,255,255,0.015)",
                padding: "clamp(24px, 3vw, 36px)", position: "relative", overflow: "hidden"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                  <span style={{ fontSize: "11px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#5a5549", fontWeight: 500 }}>AI Analysis Output</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#4ade80", animation: "float 2s infinite" }} />
                    <span style={{ fontSize: "10px", color: "#5a5549" }}>Live</span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  {[
                    { label: "Risk Score", value: "A+", sub: "Low risk" },
                    { label: "DSCR", value: "1.42x", sub: "Above threshold" },
                    { label: "Net Cash Flow", value: "$847K", sub: "Net available" },
                    { label: "Debt to Worth", value: "1.8x", sub: "Within policy" },
                  ].map((item, i) => (
                    <Reveal key={i} delay={0.08 * i}>
                      <div style={{
                        padding: "16px", background: "rgba(8,9,12,0.6)",
                        border: "1px solid rgba(255,255,255,0.04)"
                      }}>
                        <div style={{ fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#5a5549", fontWeight: 500, marginBottom: "8px" }}>{item.label}</div>
                        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "24px", fontWeight: 400, color: item.color || "#c6a35b", marginBottom: "4px" }}>{item.value}</div>
                        <div style={{ fontSize: "11px", color: "#5a5549" }}>{item.sub}</div>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ flex: "1 1 400px", minWidth: "300px", order: 2 }}>
              <Reveal>
                <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
                  <div style={{ width: "40px", height: "1px", background: "#c6a35b" }} />
                  <span style={{ fontSize: "12px", letterSpacing: "0.25em", textTransform: "uppercase", color: "#c6a35b", fontWeight: 500 }}>AI-Powered Analysis</span>
                </div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(26px, 3.5vw, 38px)", fontWeight: 400, lineHeight: 1.15, color: "#f0ece6", marginBottom: "24px" }}>
                  Intelligent credit decisions,<br /><span style={{ fontStyle: "italic", color: "#c6a35b" }}>in seconds.</span>
                </h3>
              </Reveal>
              <Reveal delay={0.1}>
                <p style={{ fontSize: "16px", color: "#8a8578", lineHeight: 1.8, fontWeight: 300, marginBottom: "32px" }}>
                  Our AI engine performs comprehensive credit analysis across all submitted financials — generating risk scores, cash flow summaries, and committee-ready memos in seconds, not days.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  {[
                    { title: "Cash Flow Analysis Engine", desc: "Aggregates personal and business cash flows with automatic entity linking and deduplication across multiple tax years." },
                    { title: "Credit Memo Generation", desc: "Produces structured, committee-ready credit memos with supporting schedules, ratio analysis, and narrative summaries." },
                    { title: "Full Audit Trail", desc: "Every step of the analysis is documented and traceable. From document intake to final memo, your team has a complete record." },
                  ].map((card, i) => (
                    <Reveal key={i} delay={0.1 * (i + 1)} direction="left">
                      <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
                        <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#c6a35b", marginTop: "8px", flexShrink: 0 }} />
                        <div>
                          <span style={{ fontSize: "14px", fontWeight: 500, color: "#f0ece6" }}>{card.title}</span>
                          <p style={{ fontSize: "13px", color: "#5a5549", lineHeight: 1.65, marginTop: "4px" }}>{card.desc}</p>
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>

          {/* Subtle divider */}
          <div style={{ height: "1px", background: "linear-gradient(90deg, transparent, rgba(198,163,91,0.12), transparent)", marginBottom: "clamp(80px, 10vw, 140px)" }} />

          {/* ── Secure Delivery ── */}
          <div style={{ display: "flex", gap: "clamp(40px, 6vw, 100px)", flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ flex: "1 1 400px", minWidth: "300px" }}>
              <Reveal>
                <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
                  <div style={{ width: "40px", height: "1px", background: "#c6a35b" }} />
                  <span style={{ fontSize: "12px", letterSpacing: "0.25em", textTransform: "uppercase", color: "#c6a35b", fontWeight: 500 }}>Secure Delivery</span>
                </div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(26px, 3.5vw, 38px)", fontWeight: 400, lineHeight: 1.15, color: "#f0ece6", marginBottom: "24px" }}>
                  Encrypted from<br /><span style={{ fontStyle: "italic", color: "#c6a35b" }}>start to finish.</span>
                </h3>
              </Reveal>
              <Reveal delay={0.1}>
                <p style={{ fontSize: "16px", color: "#8a8578", lineHeight: 1.8, fontWeight: 300, marginBottom: "32px" }}>
                  Send us your financial documents through our encrypted portal. Once our team completes the analysis, we deliver the finished credit memo and supporting schedules directly to your inbox — encrypted and committee-ready.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                  {[
                    { title: "Encrypted Upload", desc: "AES-256 encrypted portal for secure document submission" },
                    { title: "Analyst Review", desc: "Every file reviewed by a senior credit professional" },
                    { title: "Email Delivery", desc: "Completed analysis delivered to your inbox within 24 hours" },
                  ].map((item, i) => (
                    <div key={i} style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
                      <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#c6a35b", marginTop: "8px", flexShrink: 0 }} />
                      <div>
                        <span style={{ fontSize: "14px", fontWeight: 500, color: "#f0ece6" }}>{item.title}</span>
                        <span style={{ fontSize: "13px", color: "#5a5549", marginLeft: "8px" }}>{item.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            <div style={{ flex: "1 1 340px", minWidth: "280px" }}>
              <Reveal direction="left">
                <div style={{
                  border: "1px solid rgba(198,163,91,0.12)", background: "rgba(255,255,255,0.015)",
                  padding: "clamp(28px, 3vw, 40px)", position: "relative"
                }}>
                  {/* Flow steps */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
                    {[
                      { step: "01", label: "You upload documents", icon: "◈", detail: "Encrypted portal" },
                      { step: "02", label: "We analyze & build memo", icon: "◇", detail: "Senior analyst review" },
                      { step: "03", label: "We deliver via email", icon: "◉", detail: "Within 24 hours" },
                    ].map((s, i) => (
                      <div key={i}>
                        <div style={{
                          display: "flex", alignItems: "center", gap: "16px", padding: "20px 0",
                        }}>
                          <div style={{
                            width: "36px", height: "36px", display: "flex", alignItems: "center", justifyContent: "center",
                            border: "1px solid rgba(198,163,91,0.3)", background: "rgba(198,163,91,0.05)",
                            fontSize: "14px", color: "#c6a35b", flexShrink: 0
                          }}>{s.icon}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: "14px", fontWeight: 500, color: "#f0ece6", marginBottom: "2px" }}>{s.label}</div>
                            <div style={{ fontSize: "11px", color: "#5a5549" }}>{s.detail}</div>
                          </div>
                          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "16px", color: "rgba(198,163,91,0.2)", fontWeight: 400 }}>{s.step}</span>
                        </div>
                        {i < 2 && (
                          <div style={{ marginLeft: "18px", width: "1px", height: "16px", background: "linear-gradient(to bottom, rgba(198,163,91,0.2), transparent)" }} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ PROCESS ═══ */}
      <section id="process" style={{ padding: "clamp(80px, 12vw, 160px) clamp(24px, 8vw, 120px)", position: "relative" }}>
        <div style={{ position: "absolute", left: "50%", top: "0", width: "1px", height: "100%", background: "linear-gradient(to bottom, transparent, rgba(198,163,91,0.08), transparent)" }} />

        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: "72px" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
                <div style={{ width: "40px", height: "1px", background: "#c6a35b" }} />
                <span style={{ fontSize: "12px", letterSpacing: "0.25em", textTransform: "uppercase", color: "#c6a35b", fontWeight: 500 }}>How It Works</span>
                <div style={{ width: "40px", height: "1px", background: "#c6a35b" }} />
              </div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(30px, 4vw, 46px)", fontWeight: 400, color: "#f0ece6" }}>
                Three steps to <span style={{ fontStyle: "italic", color: "#c6a35b" }}>faster</span> credit decisions.
              </h2>
            </div>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "32px" }}>
            {[
              { step: "01", title: "Upload", desc: "Send financial statements through our encrypted portal. Tax returns, compiled financials, bank statements — we handle all formats.", detail: "AES-256 encryption · TLS 1.2+" },
              { step: "02", title: "Analyze", desc: "Our team spreads the financials, calculates every ratio, benchmarks against industry, and drafts your credit memorandum.", detail: "Senior analyst review on every file" },
              { step: "03", title: "Deliver", desc: "Receive completed analysis and credit-committee-ready memos within 24 hours. Revisions included at no extra cost.", detail: "24-hour guaranteed turnaround" },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 0.15}>
                <div className="step-card">
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "48px", color: "rgba(198,163,91,0.12)", fontWeight: 400, marginBottom: "20px", lineHeight: 1 }}>{s.step}</div>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "24px", fontWeight: 400, color: "#f0ece6", marginBottom: "14px" }}>{s.title}</h3>
                  <p style={{ fontSize: "14px", color: "#8a8578", lineHeight: 1.75, fontWeight: 300, marginBottom: "20px" }}>{s.desc}</p>
                  <div style={{ fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase", color: "#c6a35b", fontWeight: 500, paddingTop: "16px", borderTop: "1px solid rgba(198,163,91,0.1)" }}>{s.detail}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section id="pricing" style={{ padding: "clamp(80px, 12vw, 160px) clamp(24px, 8vw, 120px)", background: "rgba(255,255,255,0.01)", borderTop: "1px solid rgba(255,255,255,0.03)" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: "48px" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
                <div style={{ width: "40px", height: "1px", background: "#c6a35b" }} />
                <span style={{ fontSize: "12px", letterSpacing: "0.25em", textTransform: "uppercase", color: "#c6a35b", fontWeight: 500 }}>Pricing</span>
                <div style={{ width: "40px", height: "1px", background: "#c6a35b" }} />
              </div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(30px, 4vw, 46px)", fontWeight: 400, color: "#f0ece6" }}>
                Tailored to <span style={{ fontStyle: "italic", color: "#c6a35b" }}>your</span> institution.
              </h2>
              <p style={{ fontSize: "16px", color: "#5a5549", marginTop: "16px", fontWeight: 300 }}>No contracts. No minimums. No surprises.</p>
              <p style={{ fontSize: "15px", color: "#8a8578", marginTop: "12px", fontWeight: 300, lineHeight: 1.7, maxWidth: "520px", margin: "12px auto 0" }}>
                Every institution is different. Pricing is based on your portfolio size, monthly volume, and the types of analysis you need. Tell us about your lending operation and we'll build a custom quote.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            {!quoteOpen && !quoteSubmitted && (
              <div style={{ textAlign: "center" }}>
                <button className="cta-primary" onClick={() => setQuoteOpen(true)}
                  style={{ fontSize: "15px", padding: "20px 52px" }}>
                  Get Your Quote →
                </button>
              </div>
            )}

            {quoteOpen && !quoteSubmitted && (
              <div style={{
                border: "1px solid rgba(198,163,91,0.15)", background: "rgba(255,255,255,0.015)",
                padding: "clamp(32px, 5vw, 52px)", marginTop: "8px",
                animation: "fadeUp 0.5s ease"
              }}>
                <style>{`
                  .quote-input { width: 100%; padding: 14px 16px; background: rgba(8,9,12,0.8); border: 1px solid rgba(255,255,255,0.08); color: #e8e4df; font-size: 14px; font-family: 'Outfit', sans-serif; outline: none; transition: border-color 0.3s; border-radius: 2px; }
                  .quote-input:focus { border-color: rgba(198,163,91,0.5); }
                  .quote-input::placeholder { color: #4a4639; }
                  .quote-label { display: block; font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: #8a8578; margin-bottom: 8px; font-weight: 500; }
                  .type-check { display: flex; align-items: center; gap: 12px; padding: 14px 18px; border: 1px solid rgba(255,255,255,0.06); background: rgba(255,255,255,0.01); cursor: pointer; transition: all 0.25s; user-select: none; }
                  .type-check:hover { border-color: rgba(198,163,91,0.25); background: rgba(198,163,91,0.03); }
                  .type-check-active { border-color: rgba(198,163,91,0.4) !important; background: rgba(198,163,91,0.06) !important; }
                `}</style>

                {/* Bank Info Row */}
                <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "24px" }}>
                  <div style={{ flex: "1 1 calc(50% - 8px)", minWidth: "220px" }}>
                    <label className="quote-label">Your Name *</label>
                    <input className="quote-input" placeholder="John Smith" value={quoteForm.name}
                      onChange={(e) => setQuoteForm(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div style={{ flex: "1 1 calc(50% - 8px)", minWidth: "220px" }}>
                    <label className="quote-label">Email *</label>
                    <input className="quote-input" type="email" placeholder="john@acmecapital.com" value={quoteForm.email}
                      onChange={(e) => setQuoteForm(p => ({ ...p, email: e.target.value }))} />
                  </div>
                </div>

                <div style={{ marginBottom: "24px" }}>
                  <label className="quote-label">Institution Name *</label>
                  <input className="quote-input" placeholder="Acme Capital Partners" value={quoteForm.bank}
                    onChange={(e) => setQuoteForm(p => ({ ...p, bank: e.target.value }))} />
                </div>

                {/* Asset Size */}
                <div style={{ marginBottom: "24px" }}>
                  <label className="quote-label">Portfolio / Asset Size *</label>
                  <select className="quote-input" value={quoteForm.assetSize} style={{ cursor: "pointer", appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M6 8L1 3h10z' fill='%238a8578'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 16px center" }}
                    onChange={(e) => setQuoteForm(p => ({ ...p, assetSize: e.target.value }))}>
                    <option value="" style={{ background: "#0d0e12" }}>Select asset size</option>
                    <option value="under-300" style={{ background: "#0d0e12" }}>Under $300M</option>
                    <option value="300-600" style={{ background: "#0d0e12" }}>$300M – $600M</option>
                    <option value="600-1b" style={{ background: "#0d0e12" }}>$600M – $1B</option>
                    <option value="1b-3b" style={{ background: "#0d0e12" }}>$1B – $3B</option>
                    <option value="over-3b" style={{ background: "#0d0e12" }}>Over $3B</option>
                  </select>
                </div>

                {/* Monthly Volume */}
                <div style={{ marginBottom: "28px" }}>
                  <label className="quote-label">Approximate Credit Analyses Per Month *</label>
                  <select className="quote-input" value={quoteForm.monthlyVolume} style={{ cursor: "pointer", appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M6 8L1 3h10z' fill='%238a8578'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 16px center" }}
                    onChange={(e) => setQuoteForm(p => ({ ...p, monthlyVolume: e.target.value }))}>
                    <option value="" style={{ background: "#0d0e12" }}>Select monthly volume</option>
                    <option value="1-10" style={{ background: "#0d0e12" }}>1 – 10</option>
                    <option value="11-25" style={{ background: "#0d0e12" }}>11 – 25</option>
                    <option value="26-50" style={{ background: "#0d0e12" }}>26 – 50</option>
                    <option value="51-100" style={{ background: "#0d0e12" }}>51 – 100</option>
                    <option value="100+" style={{ background: "#0d0e12" }}>100+</option>
                  </select>
                </div>

                {/* Analysis Types */}
                <div style={{ marginBottom: "32px" }}>
                  <label className="quote-label" style={{ marginBottom: "12px" }}>Types of Analysis Needed *</label>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
                    {[
                      { key: "cre", label: "Commercial Real Estate", sub: "CRE, Construction, Land" },
                      { key: "ci", label: "Commercial & Industrial", sub: "C&I, Equipment, Working Capital" },
                      { key: "smallBiz", label: "Small Business", sub: "SBA, Microloans, Small C&I" },
                      { key: "consumer", label: "Consumer", sub: "Personal, Auto, Home Equity" },
                    ].map(t => (
                      <div key={t.key}
                        className={`type-check ${quoteForm.types[t.key] ? "type-check-active" : ""}`}
                        onClick={() => setQuoteForm(p => ({ ...p, types: { ...p.types, [t.key]: !p.types[t.key] } }))}>
                        <div style={{
                          width: "18px", height: "18px", borderRadius: "3px", flexShrink: 0,
                          border: `1.5px solid ${quoteForm.types[t.key] ? "#c6a35b" : "rgba(255,255,255,0.12)"}`,
                          background: quoteForm.types[t.key] ? "#c6a35b" : "transparent",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: "12px", color: "#08090c", fontWeight: 700, transition: "all 0.25s"
                        }}>{quoteForm.types[t.key] ? "✓" : ""}</div>
                        <div>
                          <div style={{ fontSize: "14px", color: quoteForm.types[t.key] ? "#f0ece6" : "#8a8578", fontWeight: 500, transition: "color 0.25s" }}>{t.label}</div>
                          <div style={{ fontSize: "11px", color: "#5a5549", marginTop: "2px" }}>{t.sub}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Submit */}
                <button className="cta-primary"
                  onClick={() => {
                    if (!quoteForm.name || !quoteForm.email || !quoteForm.bank || !quoteForm.assetSize || !quoteForm.monthlyVolume || !Object.values(quoteForm.types).some(Boolean)) {
                      alert("Please fill in all required fields.");
                      return;
                    }
                    setQuoteSubmitted(true);
                    setQuoteOpen(false);
                  }}
                  style={{ width: "100%", justifyContent: "center", fontSize: "14px", padding: "18px" }}>
                  Request My Custom Quote →
                </button>
                <p style={{ textAlign: "center", fontSize: "12px", color: "#4a4639", marginTop: "16px" }}>We respond to all quote requests within 2 hours during business days.</p>
              </div>
            )}

            {quoteSubmitted && (
              <div style={{
                textAlign: "center", padding: "60px 40px",
                border: "1px solid rgba(198,163,91,0.2)", background: "rgba(198,163,91,0.03)",
                animation: "fadeUp 0.5s ease"
              }}>
                <div style={{ fontSize: "48px", marginBottom: "20px" }}>✓</div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "24px", fontWeight: 400, color: "#f0ece6", marginBottom: "12px" }}>
                  Thank you, {quoteForm.name.split(" ")[0]}.
                </h3>
                <p style={{ fontSize: "16px", color: "#8a8578", fontWeight: 300, lineHeight: 1.7 }}>
                  We've received your quote request for {quoteForm.bank}. A member of our team will reach out to <span style={{ color: "#c6a35b" }}>{quoteForm.email}</span> within 2 hours.
                </p>
              </div>
            )}
          </Reveal>
        </div>
      </section>


      {/* ═══ SECURITY / TRUST ═══ */}
      <section id="security" style={{ padding: "clamp(80px, 12vw, 160px) clamp(24px, 8vw, 120px)" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ display: "flex", gap: "clamp(40px, 6vw, 100px)", flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ flex: "1 1 360px", minWidth: "280px" }}>
              <Reveal>
                <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
                  <div style={{ width: "40px", height: "1px", background: "#c6a35b" }} />
                  <span style={{ fontSize: "12px", letterSpacing: "0.25em", textTransform: "uppercase", color: "#c6a35b", fontWeight: 500 }}>Security</span>
                </div>
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(28px, 3.5vw, 42px)", fontWeight: 400, lineHeight: 1.15, color: "#f0ece6", marginBottom: "24px" }}>
                  Your data is protected at<br /><span style={{ fontStyle: "italic", color: "#c6a35b" }}>every step.</span>
                </h2>
                <p style={{ fontSize: "16px", color: "#8a8578", lineHeight: 1.8, fontWeight: 300 }}>
                  We maintain comprehensive cybersecurity liability insurance. Every file is encrypted in transit and at rest, every analyst is background-checked, and every access is logged.
                </p>
              </Reveal>
            </div>

            <div style={{ flex: "1 1 360px", minWidth: "280px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              {[
                { icon: "⊡", title: "Cyber Liability Insured", desc: "Comprehensive coverage" },
                { icon: "⬡", title: "AES-256 Encryption", desc: "Transit & at rest" },
                { icon: "◉", title: "Full Audit Trail", desc: "Every access logged" },
              ].map((b, i) => (
                <Reveal key={i} delay={i * 0.1} direction="left">
                  <div className="trust-badge" style={{ flexDirection: "column", alignItems: "flex-start", gap: "10px" }}>
                    <span style={{ fontSize: "22px", color: "#c6a35b" }}>{b.icon}</span>
                    <div>
                      <div style={{ fontSize: "14px", fontWeight: 500, color: "#f0ece6", marginBottom: "2px" }}>{b.title}</div>
                      <div style={{ fontSize: "12px", color: "#5a5549" }}>{b.desc}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CREDIBILITY / ABOUT ═══ */}
      <section style={{ padding: "clamp(60px, 10vw, 120px) clamp(24px, 8vw, 120px)", borderTop: "1px solid rgba(255,255,255,0.03)", background: "rgba(255,255,255,0.01)" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto", textAlign: "center" }}>
          <Reveal>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
              <div style={{ width: "40px", height: "1px", background: "#c6a35b" }} />
              <span style={{ fontSize: "12px", letterSpacing: "0.25em", textTransform: "uppercase", color: "#c6a35b", fontWeight: 500 }}>Who We Are</span>
              <div style={{ width: "40px", height: "1px", background: "#c6a35b" }} />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(28px, 3.5vw, 40px)", fontWeight: 400, lineHeight: 1.25, color: "#f0ece6", marginBottom: "24px" }}>
              Built by professionals who've sat<br />in <span style={{ fontStyle: "italic", color: "#c6a35b" }}>your</span> chair.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p style={{ fontSize: "17px", color: "#8a8578", lineHeight: 1.85, fontWeight: 300, maxWidth: "640px", margin: "0 auto" }}>
              Credit Group Solutions was founded by credit professionals who spent over a decade inside commercial banks and private lending firms — managing portfolios, writing memos, and presenting to credit committees. We're not a vendor. We're a business partner and credit department extender that embeds into your existing workflow. We combine deep banking experience with AI-powered analysis tools to deliver results that are committee-ready from day one.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══ CTA / CONTACT ═══ */}
      <section id="contact" style={{ padding: "clamp(80px, 14vw, 180px) clamp(24px, 8vw, 120px)", position: "relative" }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at center, rgba(198,163,91,0.04) 0%, transparent 70%)", pointerEvents: "none" }} />
        
        <div style={{ maxWidth: "700px", margin: "0 auto", textAlign: "center", position: "relative", zIndex: 2 }}>
          <Reveal>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(32px, 5vw, 54px)", fontWeight: 400, lineHeight: 1.1, color: "#f0ece6", marginBottom: "24px" }}>
              Start with a<br /><span style={{ fontStyle: "italic", color: "#c6a35b" }}>risk-free</span> pilot.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p style={{ fontSize: "18px", color: "#8a8578", lineHeight: 1.7, fontWeight: 300, marginBottom: "16px" }}>
              Five loans. 24-hour turnaround. $800 total.
            </p>
            <p style={{ fontSize: "15px", color: "#5a5549", lineHeight: 1.7, fontWeight: 300, marginBottom: "48px" }}>
              No contracts, no minimums, no lengthy onboarding. Send us five loans and see the difference firsthand.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "28px" }}>
              <button className="cta-primary" style={{ fontSize: "15px", padding: "20px 48px" }}
                onClick={() => window.location.href = "mailto:info@creditgroupsolutions.com?subject=Pilot%20Program%20Inquiry"}>
                Contact Us →
              </button>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "center" }}>
                <span style={{ fontSize: "15px", color: "#c6a35b", fontWeight: 500 }}>info@creditgroupsolutions.com</span>
                <span style={{ fontSize: "13px", color: "#5a5549" }}>We typically respond within 2 hours</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={{ borderTop: "1px solid rgba(255,255,255,0.04)", padding: "48px clamp(24px, 8vw, 120px)" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <div style={{ width: "30px", height: "30px", border: "1.5px solid rgba(198,163,91,0.4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "9px", color: "#c6a35b", fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>CGS</div>
            <span style={{ fontSize: "12px", letterSpacing: "0.15em", color: "#3d3a33", fontWeight: 500, textTransform: "uppercase" }}>Credit Group Solutions</span>
          </div>
          <div style={{ display: "flex", gap: "32px" }}>
            {["Services", "Platform", "Process", "Pricing", "Security"].map(s => (
              <button key={s} className="nav-link" onClick={() => scrollTo(s.toLowerCase())} style={{ fontSize: "11px" }}>{s}</button>
            ))}
          </div>
          <div style={{ fontSize: "11px", color: "#2a2820", letterSpacing: "0.05em" }}>
            © 2026 Credit Group Solutions LLC. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
